import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import CurrencyInput from "@/components/CurrencyInput";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Mail, Info } from "lucide-react";
import { formatCPF, formatCNPJ, formatPhone } from "@/lib/api";
import EmployeeVacations from "./EmployeeVacations";
import BankCombobox from "@/components/BankCombobox";
import { isBankAccountFormat, isBankAgencyFormat } from "@/data/banks";

const BANK_TYPE_OPTIONS = [
  { value: "CC", label: "Conta Corrente" },
  { value: "CP", label: "Conta Poupança" },
  { value: "SA", label: "Conta Salário" },
];

const DEFAULT_DEPARTMENTS = ["Administrativo", "Comercial", "Financeiro", "Operações", "TI"];

function DepartmentSelect({ value, onChange, departments = [] }) {
  const allDepts = [...new Set([...DEFAULT_DEPARTMENTS, ...departments])].sort();
  const isCustom = value !== "" && !allDepts.includes(value);
  const [editing, setEditing] = useState(isCustom);

  // If value was set to something not in list (custom), show input
  useEffect(() => {
    if (value === "__custom__") setEditing(true);
  }, [value]);

  if (editing) {
    return (
      <div className="flex gap-2">
        <Input data-testid="employee-department-input" value={value === "__custom__" ? "" : value}
          onChange={(e) => onChange(e.target.value)} placeholder="Digite o nome do departamento"
          className="bg-black/20 border-white/10 flex-1" autoFocus />
        <Button type="button" variant="outline" size="sm" className="border-white/10 text-xs shrink-0"
          onClick={() => { setEditing(false); onChange(""); }}>Voltar</Button>
      </div>
    );
  }

  return (
    <Select value={allDepts.includes(value) ? value : ""} onValueChange={(v) => {
      if (v === "__other__") { onChange("__custom__"); }
      else onChange(v);
    }}>
      <SelectTrigger data-testid="employee-department-select" className="bg-black/20 border-white/10">
        <SelectValue placeholder="Selecione o departamento" />
      </SelectTrigger>
      <SelectContent>
        {allDepts.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
        <SelectItem value="__other__">Outro (digitar)</SelectItem>
      </SelectContent>
    </Select>
  );
}

export default function EmployeeFormDialog({ open, onOpenChange, form, setForm, editingId, editingEmployee, isFieldLocked, onSave, saving, departments = [] }) {
  const locked = editingId && isFieldLocked();
  const bankLocked = false; // Company always has control over bank data

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg border-white/10 max-h-[85vh] overflow-y-auto" data-testid="employee-dialog">
        <DialogHeader>
          <DialogTitle>{editingId ? "Editar" : "Novo"} Colaborador</DialogTitle>
          <DialogDescription>Preencha os dados do colaborador</DialogDescription>
        </DialogHeader>
        {locked && (
          <div className="px-3 py-2 rounded-lg bg-yellow-500/5 border border-yellow-500/10 text-xs text-yellow-400" data-testid="locked-fields-notice">
            Nome, documento e celular estão bloqueados após a virada do mês de cadastro. Para alterações, solicite ao suporte.
          </div>
        )}
        <div className="grid grid-cols-2 gap-4 py-4">
          <div className="col-span-2 space-y-2">
            <Label>Tipo de Pessoa <span className="text-red-400">*</span></Label>
            {editingId ? (
              <div className="flex items-center gap-2">
                <div className={`px-3 py-2 rounded-md text-sm font-medium border ${form.type === "PJ" ? "bg-purple-500/10 border-purple-500/20 text-purple-400" : "bg-blue-500/10 border-blue-500/20 text-blue-400"}`}>
                  {form.type === "PJ" ? "Pessoa Jurídica (PJ)" : "Pessoa Física (PF)"}
                </div>
                <span className="text-xs text-muted-foreground">Tipo não pode ser alterado</span>
              </div>
            ) : (
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v, cpf_cnpj: "", socio_name: "", socio_cpf: "", name: "" })}>
                <SelectTrigger data-testid="employee-type-select" className="bg-black/20 border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="PF">Pessoa Física (PF)</SelectItem>
                  <SelectItem value="PJ">Pessoa Jurídica (PJ)</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>

          {form.type === "PF" ? (
            <>
              <div className="col-span-2 space-y-2">
                <Label>Nome Completo <span className="text-red-400">*</span></Label>
                <Input data-testid="employee-name-input" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Nome completo do colaborador" disabled={locked}
                  className={`bg-black/20 border-white/10 ${locked ? "opacity-50 cursor-not-allowed" : ""}`} />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>CPF <span className="text-red-400">*</span></Label>
                <Input data-testid="employee-cpf-input" value={form.cpf_cnpj}
                  onChange={(e) => { const d = e.target.value.replace(/\D/g, "").slice(0, 11); setForm({ ...form, cpf_cnpj: formatCPF(d) }); }}
                  placeholder="000.000.000-00" maxLength={14} disabled={locked}
                  className={`bg-black/20 border-white/10 ${locked ? "opacity-50 cursor-not-allowed" : ""}`} />
              </div>
            </>
          ) : (
            <>
              <div className="col-span-2 space-y-2">
                <Label>Razão Social <span className="text-red-400">*</span></Label>
                <Input data-testid="employee-name-input" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Razão social da empresa" disabled={locked}
                  className={`bg-black/20 border-white/10 ${locked ? "opacity-50 cursor-not-allowed" : ""}`} />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>CNPJ <span className="text-red-400">*</span></Label>
                <Input data-testid="employee-cnpj-input" value={form.cpf_cnpj}
                  onChange={(e) => { const d = e.target.value.replace(/\D/g, "").slice(0, 14); setForm({ ...form, cpf_cnpj: formatCNPJ(d) }); }}
                  placeholder="00.000.000/0000-00" maxLength={18} disabled={locked}
                  className={`bg-black/20 border-white/10 ${locked ? "opacity-50 cursor-not-allowed" : ""}`} />
              </div>
              <div className="col-span-2 mt-2 mb-1">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Sócio Representante</p>
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Nome Completo do Sócio <span className="text-red-400">*</span></Label>
                <Input data-testid="employee-socio-name-input" value={form.socio_name}
                  onChange={(e) => setForm({ ...form, socio_name: e.target.value })}
                  placeholder="Nome completo do sócio representante" disabled={locked}
                  className={`bg-black/20 border-white/10 ${locked ? "opacity-50 cursor-not-allowed" : ""}`} />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>CPF do Sócio <span className="text-red-400">*</span></Label>
                <Input data-testid="employee-socio-cpf-input" value={form.socio_cpf}
                  onChange={(e) => { const d = e.target.value.replace(/\D/g, "").slice(0, 11); setForm({ ...form, socio_cpf: formatCPF(d) }); }}
                  placeholder="000.000.000-00" maxLength={14} disabled={locked}
                  className={`bg-black/20 border-white/10 ${locked ? "opacity-50 cursor-not-allowed" : ""}`} />
              </div>
            </>
          )}

          <div className="col-span-2 space-y-2">
            <Label>Email</Label>
            <Input data-testid="employee-email-input" type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="bg-black/20 border-white/10" />
          </div>
          <div className="col-span-2 space-y-2">
            <Label>Salário mensal (R$) <span className="text-red-400">*</span></Label>
            <CurrencyInput data-testid="employee-salary-input"
              value={form.salary} onChange={(v) => setForm({ ...form, salary: v })}
              placeholder="10.000,00" className="bg-black/20 border-white/10" />
          </div>
          {form.type === "PJ" && (
            <div className="col-span-2 grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Retenção na fonte?</Label>
                <Select value={form.has_retention ? "sim" : "nao"} onValueChange={(v) => setForm({ ...form, has_retention: v === "sim", retention_pct: v === "nao" ? "" : form.retention_pct })}>
                  <SelectTrigger data-testid="employee-retention-select" className="bg-black/20 border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nao">Não</SelectItem>
                    <SelectItem value="sim">Sim</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.has_retention && (
                <div className="space-y-2">
                  <Label>Percentual retido (%)</Label>
                  <Input data-testid="employee-retention-pct" type="number" step="0.01" min="0" max="100"
                    value={form.retention_pct}
                    onChange={(e) => setForm({ ...form, retention_pct: e.target.value })}
                    placeholder="Ex: 11.00" className="bg-black/20 border-white/10" />
                </div>
              )}
            </div>
          )}
          {/* Payment method + Bank data */}
          <div className="col-span-2 mt-2 mb-1">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Forma de Pagamento</p>
          </div>
          <div className="col-span-2">
            <div className="flex gap-2">
              <button type="button" onClick={() => !bankLocked && setForm({ ...form, payment_method: "pix" })}
                data-testid="employee-payment-method-pix-button"
                className={`flex-1 p-3 rounded-lg border text-sm font-medium text-center transition-colors ${form.payment_method === "pix" ? "border-blue-500/40 bg-blue-500/10 text-blue-400" : "border-white/10 bg-black/10 text-muted-foreground hover:bg-black/20"} ${bankLocked ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}>
                Chave PIX (CPF/CNPJ)
              </button>
              <button type="button" onClick={() => !bankLocked && setForm({ ...form, payment_method: "bank" })}
                data-testid="employee-payment-method-bank-button"
                className={`flex-1 p-3 rounded-lg border text-sm font-medium text-center transition-colors ${form.payment_method !== "pix" ? "border-blue-500/40 bg-blue-500/10 text-blue-400" : "border-white/10 bg-black/10 text-muted-foreground hover:bg-black/20"} ${bankLocked ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}>
                Conta Bancária
              </button>
            </div>
            {bankLocked && <p className="text-[10px] text-yellow-400 mt-1">Bloqueado — só o colaborador pode alterar após o primeiro acesso</p>}
          </div>

          {form.payment_method === "pix" ? (
            <div className="col-span-2 p-3 rounded-lg bg-blue-500/5 border border-blue-500/10">
              <p className="text-sm text-blue-400 font-medium">
                {form.type === "PJ" ? "CNPJ da empresa" : "CPF do colaborador"} será usado como chave PIX
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {form.cpf_cnpj || "Preencha o documento acima"}
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label className="text-xs">Banco <span className="text-red-400">*</span></Label>
                <BankCombobox value={form.bank_code || ""} onChange={(v) => setForm({ ...form, bank_code: v })} disabled={bankLocked} testId="employee-bank-code" placeholder="Selecione..." />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Tipo de Conta <span className="text-red-400">*</span></Label>
                <Select value={form.bank_type || ""} onValueChange={(v) => setForm({ ...form, bank_type: v })} disabled={bankLocked}>
                  <SelectTrigger className={`bg-black/20 border-white/10 ${bankLocked ? "opacity-50" : ""}`}><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {BANK_TYPE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {/* Agência e conta entram CRUAS (A-R3e-xv): nem `onChange` que
                  reescreve, nem `maxLength` que corta. O saneador que morava
                  aqui (`formatBankAccount`) fazia `replace(/\D/g,"")` e
                  reinterpretava o último dígito como DV — "12 345-6" virava
                  "12345-6", uma conta plausível e de outra pessoa, sem o
                  operador ver nada. O valor fora do formato é MOSTRADO como
                  está e destacado; quem BLOQUEIA o save é `CompanyEmployees`,
                  com toast que diz o campo e o formato. */}
              <div className="space-y-2">
                <Label className="text-xs">Agência <span className="text-red-400">*</span></Label>
                <Input data-testid="employee-bank-agency-input" value={form.bank_agency || ""} onChange={(e) => setForm({ ...form, bank_agency: e.target.value })}
                  placeholder="0001" disabled={bankLocked}
                  className={`bg-black/20 border-white/10 ${bankLocked ? "opacity-50" : ""} ${form.bank_agency && !isBankAgencyFormat(form.bank_agency) ? "border-red-500/50" : ""}`} />
                {form.bank_agency && !isBankAgencyFormat(form.bank_agency) && (
                  <p className="text-[11px] text-red-400" data-testid="employee-bank-agency-error">
                    Use de 1 a 5 dígitos, sem o dígito verificador (ex.: 0001)
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Conta <span className="text-red-400">*</span></Label>
                <Input data-testid="employee-bank-account-input" value={form.bank_account || ""} onChange={(e) => setForm({ ...form, bank_account: e.target.value })}
                  placeholder="12345-6" disabled={bankLocked}
                  className={`bg-black/20 border-white/10 ${bankLocked ? "opacity-50" : ""} ${form.bank_account && !isBankAccountFormat(form.bank_account) ? "border-red-500/50" : ""}`} />
                {form.bank_account && !isBankAccountFormat(form.bank_account) && (
                  <p className="text-[11px] text-red-400" data-testid="employee-bank-account-error">
                    Use até 12 dígitos e o dígito verificador após um único hífen (ex.: 12345-6)
                  </p>
                )}
              </div>
            </>
          )}

          <div className="space-y-2">
            <Label>Departamento / Área <span className="text-red-400">*</span></Label>
            <DepartmentSelect value={form.department} onChange={(v) => setForm({ ...form, department: v })} departments={departments} />
          </div>
          <div className="space-y-2">
            <Label>Data de contratação</Label>
            <Input data-testid="employee-hire-date-input" type="date" value={form.hire_date}
              onChange={(e) => setForm({ ...form, hire_date: e.target.value })}
              className="bg-black/20 border-white/10" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label>Requer aprovação manual?</Label>
              <TooltipProvider><Tooltip><TooltipTrigger asChild>
                <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
              </TooltipTrigger><TooltipContent side="top" className="max-w-[280px] text-xs">
                <p>Quando ativada, toda antecipação deste colaborador precisa ser aprovada manualmente pela empresa antes de ser processada. Quando desativado, toda antecipação deste colaborador que estiver dentro das políticas é aprovada sem segunda checagem pela empresa. "Seguir Padrão" segue a configuração geral da empresa que está em configurações.</p>
              </TooltipContent></Tooltip></TooltipProvider>
            </div>
            <div className="flex items-center gap-2">
              <Select value={form.approval_required === true ? "enabled" : form.approval_required === false ? "disabled" : "inherit"}
                onValueChange={(v) => setForm({ ...form, approval_required: v === "enabled" ? true : v === "disabled" ? false : null })}>
                <SelectTrigger data-testid="employee-approval-select" className="bg-black/20 border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="inherit">Seguir Padrão</SelectItem>
                  <SelectItem value="enabled">Ativada</SelectItem>
                  <SelectItem value="disabled">Desativada</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label>Custódia de pagamento</Label>
              <TooltipProvider><Tooltip><TooltipTrigger asChild>
                <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
              </TooltipTrigger><TooltipContent side="top" className="max-w-[280px] text-xs">
                <p>Quando ativada, o valor pago a este colaborador fica retido (em custódia) até ser liberado manualmente no fechamento. Quando desativado, após o boleto da folha mensal ser pago o valor já é liberado. "Seguir Padrão" segue a configuração geral da empresa que está em configurações.</p>
              </TooltipContent></Tooltip></TooltipProvider>
            </div>
            <Select value={form.custody_override || "inherit"} onValueChange={(v) => setForm({ ...form, custody_override: v })}>
              <SelectTrigger data-testid="employee-custody-select" className="bg-black/20 border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="inherit">Seguir Padrão</SelectItem>
                <SelectItem value="enabled">Ativada</SelectItem>
                <SelectItem value="disabled">Desativada</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Celular <span className="text-red-400">*</span></Label>
            <Input data-testid="employee-phone-input" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: formatPhone(e.target.value) })}
              placeholder="(11) 99999-9999" maxLength={15} disabled={locked}
              className={`bg-black/20 border-white/10 ${locked ? "opacity-50 cursor-not-allowed" : ""}`} />
          </div>
          {!editingId && (
            <div className="col-span-2 flex items-center gap-3 p-3 rounded-lg bg-blue-500/5 border border-blue-500/10">
              <Checkbox data-testid="employee-notification-checkbox"
                checked={form.send_notification}
                onCheckedChange={(v) => setForm({ ...form, send_notification: !!v })} />
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-blue-400" />
                <span className="text-sm">Enviar notificação de cadastro por email</span>
              </div>
            </div>
          )}
          {editingId && (
            <EmployeeVacations employeeId={editingId} canEdit={!locked} />
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-white/10">Cancelar</Button>
          <Button data-testid="save-employee-btn" onClick={onSave} disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white">
            {saving ? <div className="loading-spinner" /> : editingId ? "Salvar" : "Criar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
